import type { WirePipeline } from '@/api/hooks';
import type { WireCostSummary } from '@/api/wire';
import { dayOf, daysUntil, nowOf, others, plural, type Viewer } from '@/directory/explainKit';
import { clock } from '@/today/copy';
import type { ItemContext } from '@/ui/context';
import { usd } from '@/ui/meter';
import type { Who } from '@/ui/who';
import { humanise } from '@/ui/words';

/**
 * What a row of an insight view says about itself (ADR-0155). An insight
 * row is a bar and a number; the number is only useful with what it is
 * measured from, who it concerns and what to do about it. Every figure here
 * is one the view already holds: the cost ledger's, the department's own
 * Deal records, the person's own bookable slots. Nothing is estimated, and
 * a contact that has gone quiet is explained by the directory's own builder
 * so the list and the record say the same thing.
 */

// --- spend -------------------------------------------------------------------------------

export function explainSpend(d: WireCostSummary['byDepartment'][number], total: Pick<WireCostSummary, 'totalUsd' | 'days'>, rank: number, who: Who): ItemContext {
  const name = who.label(d.department);
  const share = total.totalUsd > 0 ? Math.round((d.spentUsd / total.totalUsd) * 100) : 0;
  const idle = d.spentUsd > 0 && d.shipped === 0;
  const next = ((): ItemContext['next'] => {
    if (d.spentUsd === 0) return { tip: `Nothing is needed. ${name} spent nothing on models in these ${total.days} days.`, instead: 'If it should have been working, open it and check its agents are running.' };
    if (idle) {
      const lead = who.lead(d.department);
      return {
        tip: `${name} spent ${usd(d.spentUsd)} and landed no change in ${total.days} days. Worth a conversation with ${lead && lead.name !== 'You' ? lead.name : 'its lead'} about what the spend is for; not an alarm.`,
        instead: 'Or open the department to see what its agents are doing and whether a budget is set.',
      };
    }
    if (rank === 0) return { tip: `${name} spends the most${d.perChangeUsd !== null ? `, at ${usd(d.perChangeUsd)} per change` : ''}. Nothing is needed unless that surprises you; compare it with the others below.`, instead: 'Open the department to see where it goes, agent by agent.' };
    return { tip: `Nothing is needed. ${name} spent ${usd(d.spentUsd)}${d.perChangeUsd !== null ? `, at ${usd(d.perChangeUsd)} per change` : ''}; it is in line with the rest unless you know better.`, instead: 'Open the department to see where it goes.' };
  })();
  return {
    subject: { kind: 'spend', title: `${name}’s model spend` },
    summary: `${name} spent ${usd(d.spentUsd)} on models in the last ${total.days} days: ${plural(d.calls, 'call')}, ${share}% of the company’s ${usd(total.totalUsd)}.${d.shipped > 0 ? ` It landed ${plural(d.shipped, 'change')}.` : ''}`,
    need: idle ? 'A word with its lead about what the spend bought.' : 'Nothing.',
    why: 'This is the Control room’s view across departments, so a superadmin sees where money goes without opening each one.',
    source: { label: 'The cost ledger, written as each call was made. Nothing here is estimated.' },
    broughtBy: null,
    owner: who.lead(d.department),
    involved: who.agentsOf(d.department),
    knows: others(who.knowers(d.department)),
    facts: [
      ['Spent', usd(d.spentUsd)],
      ['Calls', String(d.calls)],
      ['Changes landed', String(d.shipped)],
      ['Per change', d.perChangeUsd !== null ? usd(d.perChangeUsd) : null],
      ['Share of the total', `${share}%`],
    ],
    next,
    open: { to: `/departments/${encodeURIComponent(d.department)}`, label: 'Open the department' },
  };
}

// --- pipeline ----------------------------------------------------------------------------

const money = (totals: Array<{ currency: string; total: string }>): string =>
  totals.length === 0 ? '—' : totals.slice().sort((a, b) => a.currency.localeCompare(b.currency)).map((t) => `${Number(t.total).toLocaleString('en-GB', { maximumFractionDigits: 0 })} ${t.currency}`).join(' · ');

export function explainPipelineStatus(s: WirePipeline['statuses'][number], p: WirePipeline, who: Who, v: Viewer, department: string): ItemContext {
  const status = humanise(s.status);
  const stale = p.stale.filter((d) => d.status === s.status);
  const closed = s.status === 'won' || s.status === 'lost';
  const next = ((): ItemContext['next'] => {
    if (s.status === 'won') return { tip: 'Nothing to do. These are won and the department’s order capture has run for them.', instead: 'Open the deals to read any one of them.' };
    if (s.status === 'lost') {
      const why = (p.lostWhy ?? []).filter((r) => r.count > 0).slice(0, 2).map((r) => `${r.reason ?? 'no reason given'} (${r.count})`);
      return {
        tip: why.length > 0 ? `Look for the pattern: deals were lost because of ${why.join(' and ')}. If the same reason repeats, it is a conversation for the department’s lead, not for one deal.` : 'No reason was recorded for these. Next time a deal is marked Lost, give the reason: the pipeline can only say why when someone has said.',
        instead: 'Nothing here can be reopened; a customer who comes back is a new deal.',
      };
    }
    if (stale.length > 0) {
      const first = stale[0]!;
      return {
        tip: `${plural(stale.length, 'deal')} here ${stale.length === 1 ? 'has' : 'have'} not moved in ${p.staleAfterDays} days${first.title ? `, starting with “${first.title}”${first.company ? ` (${first.company})` : ''}` : ''}. Ask whoever holds the account where it stands, then mark it Won or Lost.`,
        instead: 'Or leave them: nothing closes by itself, but they will keep showing as stale.',
      };
    }
    return { tip: 'Nothing is needed. Nothing here has stood still long enough to ask about; mark a deal Won or Lost from its page when the customer decides.', instead: undefined };
  })();
  return {
    subject: { kind: 'pipeline stage', title: `${status} deals` },
    summary: `${plural(s.count, 'deal')} ${closed ? 'marked' : 'are'} ${s.status}, worth ${money(s.totals)}. Totals are per currency and never converted.`,
    need: closed ? 'Nothing.' : stale.length > 0 ? 'An update on the stale ones from whoever holds the account.' : 'Nothing right now.',
    why: `This is ${who.label(department)}’s own pipeline, counted from its Deal records; another department’s is never shown here.`,
    source: { label: 'The department’s own Deal records, counted when you opened this page' },
    broughtBy: null,
    involved: who.agentsOf(department),
    knows: others(who.knowers(department)),
    facts: [
      ['Deals', String(s.count)],
      ['Worth', money(s.totals)],
      ['Not moved for a long time', stale.length > 0 ? String(stale.length) : null],
    ],
    next,
    open: { to: '/records/Deal', label: 'Open the deals' },
  };
}

// --- open slots ---------------------------------------------------------------------------

export function explainStretch(w: { from: string; to: string; minutes: number }, longest: number, who: Who, v: Viewer): ItemContext {
  const now = nowOf(v);
  const day = new Date(w.from).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
  const today = daysUntil(w.from, now) === 0;
  const span = `${clock(w.from)}–${clock(w.to)}`;
  const long = w.minutes >= 90;
  return {
    subject: { kind: 'free stretch', title: `${day}, ${span}` },
    summary: `You are free ${day}, ${span}: ${w.minutes < 60 ? `${w.minutes} minutes` : `${Math.floor(w.minutes / 60)} h${w.minutes % 60 ? ` ${w.minutes % 60} min` : ''}`}, inside the hours you said you can be booked and with nothing else on your calendar.`,
    need: 'Your choice: hold it for something, or leave it open.',
    why: 'A free calendar is not a willing one, so only the hours you set are offered; this is what is left of them.',
    source: { label: 'Your calendar and your bookable hours, read just now' },
    broughtBy: null,
    owner: who.me,
    knows: undefined,
    facts: [['Longest this week', w.minutes === longest ? 'Yes' : null], ['Day', dayOf(w.from)]],
    next: {
      tip: today
        ? `It is today, so hold it now or it passes. Press Hold it, say what it is for, and your twin writes it on your calendar with nobody else on it.`
        : long
          ? 'A good stretch for something that needs real time: a long call or focused work. Hold it and your twin writes it on your calendar with nobody else on it.'
          : 'A short gap, fine for a quick call. Hold it and your twin writes it on your calendar with nobody else on it.',
      instead: 'Nobody is told when you hold a time. If you add people to it, they are invited only when you press Send invitations.',
    },
  };
}
